import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useIsB2B } from 'components/B2BComponents/useIsB2B';
import { useDisclosure } from 'components/Dialog';
import { TAB_LABOR_RATE_SELECTION } from 'domains/easyBasket/EasyBasket.types';
import { getEasyBasketEstimateCreationStatus } from 'domains/estimate/Estimate.store';
import { getCurrency } from 'domains/user';
import { TotalStripDialog } from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/common/TotalStripDialog';
import { StickyStrip } from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/OperationsSection.styled';
import {
  useIsLaborTimeSelectionValid,
  useTotalLaborTimePrice,
  useTotalPrice,
} from 'pages/CatalogPage/DH/SubcategorySection/OperationsSection/utils';
import { BlackButton, CenteredSpin, Flex, Pipeline, Text, WithTooltip } from 'UI';
import { isLoading, textFormatter } from 'utils';

export interface LaborTimesTotalStripProps {
  onClickNext: () => void;
}

const LaborTimesTotalStrip = ({ onClickNext }: LaborTimesTotalStripProps) => {
  const { t } = useTranslation();
  const disclosure = useDisclosure();
  const isB2B = useIsB2B();
  const totalLaborTimesPrice = useTotalLaborTimePrice();
  const isLaborTimeSelectionValid = useIsLaborTimeSelectionValid();
  const { totalVatIncluded, totalVatExcluded } = useTotalPrice();
  const currency = useSelector(getCurrency);
  const easyBasketEstimateCreationStatus = useSelector(getEasyBasketEstimateCreationStatus);
  const isGeneratingEstimate = isLoading(easyBasketEstimateCreationStatus?.status);
  const { onOpen, onClose } = disclosure;

  const handleClickOnNext = () => {
    if (isGeneratingEstimate) return;
    if (isLaborTimeSelectionValid) {
      onClickNext();
    } else {
      onOpen();
    }
  };

  const isButtonDisabled = isB2B || isGeneratingEstimate;

  return (
    <StickyStrip>
      <TotalStripDialog
        tab={TAB_LABOR_RATE_SELECTION}
        disclosure={disclosure}
        onClose={onClose}
        onClickNext={onClickNext}
      />
      <Flex align={'center'} gap={15}>
        <Flex direction={'column'} size={0} minWidth={200} align={'center'}>
          <Flex direction={'column'}>
            <Text type="lg" bold disableGutter noWrap displayStyle={'disabled'}>
              {t('common.price.vat_include_price', '{{vat_include_price}} VAT. Incl', {
                vat_include_price: textFormatter.formatCurrency(totalLaborTimesPrice.totalVatIncluded, currency),
              })}
            </Text>
            <Text type="sm" disableGutter noWrap displayStyle={'disabled'}>
              {'Labor time Total'}
            </Text>
          </Flex>
        </Flex>
        <Pipeline size={55} />
        <Flex maxWidth={50} />
        <Flex direction={'column'}>
          <Text type="heading4">
            {t('catalog.easy_basket.tab.total.operation_total.title', 'Total Operation Cost')}
          </Text>
          <Text type="base">
            {t(
              'catalog.easy_basket.tab.total.operation_total.description',
              'Including Parts and Labor time  for the Operation',
            )}
          </Text>
        </Flex>
        <Pipeline size={55} />
        <Flex direction={'column'} size={0} minWidth={200} align={'center'}>
          <Flex direction={'column'}>
            <Text type="lg" bold disableGutter noWrap>
              {t('common.price.vat_include_price', '{{vat_include_price}} VAT. Incl', {
                vat_include_price: textFormatter.formatCurrency(totalVatIncluded, currency),
              })}
            </Text>
            <Text type="sm" disableGutter noWrap>
              {t('common.price.or_vat_exclude_price', 'or {{vat_exclude_price}} VAT. Excl', {
                vat_exclude_price: textFormatter.formatCurrency(totalVatExcluded, currency),
              })}
            </Text>
          </Flex>
        </Flex>
        <Pipeline size={55} />
        <Flex justify={'center'} size={0} minWidth={250}>
          <WithTooltip
            title={t(
              'catalog.easy_basket.action.generate_estimate.description',
              'Generated estimate will include Parts and Labor times for this operation',
            )}
          >
            <div>
              <BlackButton size={'large'} onClick={handleClickOnNext} disabled={isButtonDisabled}>
                {isGeneratingEstimate && <CenteredSpin />}
                <Flex minWidth={150} align={'center'} justify={'center'}>
                  {t('catalog.easy_basket.action.generate_estimate.title', 'Generate Estimate')}
                </Flex>
              </BlackButton>
            </div>
          </WithTooltip>
        </Flex>
      </Flex>
    </StickyStrip>
  );
};

export default LaborTimesTotalStrip;
